import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

import { getRecoil } from "recoil-nexus";
import organizationIdSelector from "../atoms/organizationIdSelector";
import OrganizationData from "../data/OrganizationData";
import Emitter from "../Emitter";

export const AdminDraftContext = React.createContext(null);

export function useAdminDraftOptional() {
  return React.useContext(AdminDraftContext);
}

function stripDraftMeta(d) {
  if (!d || typeof d !== "object") return d;
  const o = { ...d };
  delete o.updatedAt;
  return o;
}

function hasAnyDirty(d) {
  return d.org || d.weekly || d.daily || d.photos || d.labels;
}

/**
 * True if workingDraft has any section the user could publish (non-empty content).
 */
export function draftContentHasMeaningfulKeys(w) {
  if (!w || typeof w !== "object") return false;
  for (const k of Object.keys(w)) {
    if (k === "updatedAt") continue;
    const v = w[k];
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) return true;
    if (typeof v === "object") {
      if (Object.keys(v).length === 0) continue;
      return true;
    }
    return true;
  }
  return false;
}

/**
 * Unified admin draft: General, Report Questions, Daily Questions, Progress Photos, Labels.
 * Draft persists in coachConsoleAdminDraft (Firestore); Publish applies to live. Revert clears draft only.
 */
export function AdminDraftProvider({ uid, children }) {
  const organizationId = getRecoil(organizationIdSelector);
  const [liveOrg, setLiveOrg] = React.useState(null);
  const [workingDraft, setWorkingDraft] = React.useState({});
  const [dirty, setDirty] = React.useState({
    org: false,
    weekly: false,
    daily: false,
    photos: false,
    labels: false,
  });
  const dirtyRef = React.useRef(dirty);
  React.useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const [publishing, setPublishing] = React.useState(false);
  const [reverting, setReverting] = React.useState(false);
  /** Increments only after a successful debounced autosave (for toast; not tied to snapshot churn). */
  const [draftAutosaveTick, setDraftAutosaveTick] = React.useState(0);

  React.useEffect(() => {
    if (!organizationId) return undefined;
    const ref = firebase.firestore().collection("organizations").doc(organizationId);
    const unsub = ref.onSnapshot(
      (snap) => {
        const data = snap.data() || {};
        const merged = { id: snap.id, ...data };
        setLiveOrg(merged);
        if (!hasAnyDirty(dirtyRef.current)) {
          const serverDraft = data.coachConsoleAdminDraft;
          const legacyOrg = stripDraftMeta(data.coachConsoleOrgDraft);
          const next = {};
          if (serverDraft && typeof serverDraft === "object") {
            const sd = { ...serverDraft };
            delete sd.updatedAt;
            Object.assign(next, sd);
          }
          if (legacyOrg && !next.organization) {
            next.organization = legacyOrg;
          }
          setWorkingDraft(next);
        }
      },
      () => {},
    );
    return () => unsub();
  }, [organizationId]);

  /** null until first Firestore snapshot — avoids syncing empty {} into General then re-filling (flash). */
  const mergeOrgForGeneral = React.useMemo(() => {
    if (!liveOrg) return null;
    const orgPart = workingDraft.organization;
    if (!orgPart) return liveOrg;
    return { ...liveOrg, ...orgPart };
  }, [liveOrg, workingDraft.organization]);

  const hasDraftContent = React.useMemo(() => {
    if (hasAnyDirty(dirty)) return true;
    return draftContentHasMeaningfulKeys(workingDraft);
  }, [dirty, workingDraft]);

  const updateOrganization = React.useCallback((payload) => {
    setWorkingDraft((w) => ({
      ...w,
      organization: { ...(w.organization || {}), ...payload },
    }));
    setDirty((d) => ({ ...d, org: true }));
  }, []);

  const updateWeeklyOptional = React.useCallback((rows) => {
    setWorkingDraft((w) => ({ ...w, weeklyOptional: rows }));
    setDirty((d) => ({ ...d, weekly: true }));
  }, []);

  const updateOptional = React.useCallback((rows) => {
    setWorkingDraft((w) => ({ ...w, optional: rows }));
    setDirty((d) => ({ ...d, daily: true }));
  }, []);

  const updateProgressPhotoConfiguration = React.useCallback((rows) => {
    setWorkingDraft((w) => ({ ...w, progressPhotoConfiguration: rows }));
    setDirty((d) => ({ ...d, photos: true }));
  }, []);

  const updateLabels = React.useCallback((rows) => {
    setWorkingDraft((w) => ({ ...w, labels: rows }));
    setDirty((d) => ({ ...d, labels: true }));
  }, []);

  const persistTimerRef = React.useRef(null);
  React.useEffect(() => {
    if (!uid || !hasAnyDirty(dirty)) return undefined;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => {
      (async () => {
        try {
          const ok = await OrganizationData.saveCoachConsoleAdminDraft(
            uid,
            workingDraft,
          );
          if (ok) {
            setDraftAutosaveTick((n) => n + 1);
          }
        } catch (e) {
          /* ignore */
        }
      })();
    }, 2000);
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [uid, workingDraft, dirty]);

  const publishAll = React.useCallback(async () => {
    if (!uid || !hasDraftContent) return false;
    setPublishing(true);
    try {
      await OrganizationData.saveCoachConsoleAdminDraft(uid, workingDraft);
      const ok = await OrganizationData.publishCoachConsoleAdminDraft(
        uid,
        workingDraft,
      );
      if (ok) {
        Emitter.emit("orgChanged");
        setDirty({
          org: false,
          weekly: false,
          daily: false,
          photos: false,
          labels: false,
        });
        setWorkingDraft({});
      }
      return ok;
    } finally {
      setPublishing(false);
    }
  }, [uid, hasDraftContent, workingDraft]);

  const revertDraft = React.useCallback(async () => {
    if (!uid) return false;
    setReverting(true);
    try {
      const ok = await OrganizationData.clearCoachConsoleAdminDraft(uid);
      if (ok) {
        Emitter.emit("orgChanged");
        setDirty({
          org: false,
          weekly: false,
          daily: false,
          photos: false,
          labels: false,
        });
        setWorkingDraft({});
      }
      return ok;
    } finally {
      setReverting(false);
    }
  }, [uid]);

  const value = React.useMemo(
    () => ({
      liveOrg,
      workingDraft,
      mergeOrgForGeneral,
      dirty,
      /** True when there is a draft to publish (persisted and/or unsaved local edits). */
      hasDraftContent,
      /** @deprecated use hasDraftContent */
      hasPendingPublish: hasDraftContent,
      draftAutosaveTick,
      publishing,
      reverting,
      updateOrganization,
      updateWeeklyOptional,
      updateOptional,
      updateProgressPhotoConfiguration,
      updateLabels,
      publishAll,
      revertDraft,
    }),
    [
      liveOrg,
      workingDraft,
      mergeOrgForGeneral,
      dirty,
      hasDraftContent,
      draftAutosaveTick,
      publishing,
      reverting,
      updateOrganization,
      updateWeeklyOptional,
      updateOptional,
      updateProgressPhotoConfiguration,
      updateLabels,
      publishAll,
      revertDraft,
    ],
  );

  return (
    <AdminDraftContext.Provider value={value}>{children}</AdminDraftContext.Provider>
  );
}
